import { isNil, isString } from 'lodash';

import type { Maybe } from './globals';
import type { DateDisplayFormat, ISettings, TimeDisplayFormat } from './settings';

/**
 * Company date/time display preferences derived from ISO 3166-1 alpha-3 country.
 * Used when seeding `company.settings` on create (and backfill).
 *
 * Source: SUR-118 operating-countries table. Weekday labels still follow UI locale;
 * these values only control numeric order and 12h/24h.
 */
export type CompanyDisplayPreferences = {
  dateDisplayFormat: DateDisplayFormat;
  timeDisplayFormat: TimeDisplayFormat;
};

/**
 * Product default when country is null/unknown — matches {@link EntitySettings.Company}
 * schema defaults (`MDY` + `twelveHour`), not the USA operating-country row (`weekdayMDY`).
 */
export const DEFAULT_COMPANY_DISPLAY_PREFERENCES: Readonly<CompanyDisplayPreferences> =
  Object.freeze({
    dateDisplayFormat: 'MDY',
    timeDisplayFormat: 'twelveHour'
  });

/**
 * Country → preferences map for Nova operating countries.
 *
 * Canada (CAN): English and French share `weekdayYMD` but differ on time (12h vs 24h).
 * Country alone cannot distinguish locale, so CAN uses the English default (`twelveHour`).
 */
export const COMPANY_DISPLAY_PREFERENCES_BY_COUNTRY: Readonly<
  Record<string, Readonly<CompanyDisplayPreferences>>
> = Object.freeze({
  USA: Object.freeze({ dateDisplayFormat: 'weekdayMDY', timeDisplayFormat: 'twelveHour' }),
  CAN: Object.freeze({ dateDisplayFormat: 'weekdayYMD', timeDisplayFormat: 'twelveHour' }),
  GBR: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twelveHour' }),
  MEX: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twelveHour' }),
  CHE: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twentyFourHour' }),
  AUS: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twelveHour' }),
  BEL: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twentyFourHour' }),
  SLV: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twelveHour' }),
  MLT: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twelveHour' }),
  ESP: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twentyFourHour' }),
  SWE: Object.freeze({ dateDisplayFormat: 'weekdayYMD', timeDisplayFormat: 'twentyFourHour' }),
  DEU: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twentyFourHour' }),
  COL: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twelveHour' }),
  NZL: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twelveHour' }),
  SGP: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twelveHour' }),
  IRL: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twelveHour' }),
  NLD: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twentyFourHour' }),
  POL: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twentyFourHour' }),
  FRA: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twentyFourHour' }),
  BRA: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twentyFourHour' }),
  JPN: Object.freeze({ dateDisplayFormat: 'weekdayYMD', timeDisplayFormat: 'twentyFourHour' }),
  CRI: Object.freeze({ dateDisplayFormat: 'weekdayDMY', timeDisplayFormat: 'twelveHour' })
});

/** Returns country-based display preferences; unknown/null/blank country → product defaults. */
export function getCompanyDisplayPreferencesForCountry(
  country: Maybe<string>
): CompanyDisplayPreferences {
  if (!isString(country) || country.trim().length === 0) {
    return { ...DEFAULT_COMPANY_DISPLAY_PREFERENCES };
  }

  const normalizedCountry = country.trim().toUpperCase();
  const preferencesForCountry = COMPANY_DISPLAY_PREFERENCES_BY_COUNTRY[normalizedCountry];
  if (isNil(preferencesForCountry)) {
    return { ...DEFAULT_COMPANY_DISPLAY_PREFERENCES };
  }

  return { ...preferencesForCountry };
}

/**
 * Fills missing `dateDisplayFormat` / `timeDisplayFormat` from country defaults.
 * Explicit values on `settings` are preserved. Does not mutate the input.
 */
export function withDefaultCompanyDisplayPreferences(
  country: Maybe<string>,
  settings?: Maybe<ISettings>
): ISettings {
  const countryPreferences = getCompanyDisplayPreferencesForCountry(country);
  const existingSettings = settings ?? {};

  return {
    ...existingSettings,
    dateDisplayFormat: !isNil(existingSettings.dateDisplayFormat)
      ? existingSettings.dateDisplayFormat
      : countryPreferences.dateDisplayFormat,
    timeDisplayFormat: !isNil(existingSettings.timeDisplayFormat)
      ? existingSettings.timeDisplayFormat
      : countryPreferences.timeDisplayFormat
  };
}
